import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, reset } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';
import { Typography, CircularProgress, Switch } from '@material-ui/core';
import moment from 'moment';
import styles from './styles';
import Button from '../../../Button';
import formFields from './formFields';
import {
  FORMS,
  VIEWABLE_FORM_MODES,
  USER_PROFILE_TYPES,
  SERVICE_AREAS,
} from '../../../../constants';
import {
  selectIsUpdatingPersonalProfile,
  selectPersonalProfileFormMode,
  selectBusinessAttributes,
} from '../../../../store/selectors';
import {
  editUserProfile,
  editUserProfileCancelled,
  updateUserProfile,
} from '../../../../store/actions/userProfile';
import { NOTIFICATION_STATUSES } from '../../../NotificationSettingsContainer';
import { preparePhoneNumberForBackend } from '../../../../utilities/helpers';

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    actionsRow: PropTypes.string,
  }).isRequired,
  initialValues: PropTypes.object.isRequired,
  isUpdatingPersonalProfile: PropTypes.bool.isRequired,
  personalProfileFormMode: PropTypes.oneOf([
    VIEWABLE_FORM_MODES.EDITING,
    VIEWABLE_FORM_MODES.VIEWING,
  ]).isRequired,
  onEditUserProfile: PropTypes.func.isRequired,
  onEditUserProfileCancelled: PropTypes.func.isRequired,
  onResetForm: PropTypes.func.isRequired,
  onUpdateUserProfile: PropTypes.func.isRequired,
  enableEditing: PropTypes.bool.isRequired,
  businessAttributes: PropTypes.shape({
    businessNumber: PropTypes.string,
    businessName: PropTypes.string,
    businessMailingAddress: PropTypes.string,
    businessDescription: PropTypes.string,
    businessPhoneNumber: PropTypes.string,
    businessServiceAreaAddress: PropTypes.string,
    businessServiceAreaCoordinates: PropTypes.string,
    businessSkillsetBuild: PropTypes.string,
    businessSkillsetLook: PropTypes.string,
    businessPosition: PropTypes.string,
    businessApproved: PropTypes.int,
    businessServiceAreaState: PropTypes.string,
    businessServiceAreaType: PropTypes.oneOf([
      SERVICE_AREAS.LOCAL,
      SERVICE_AREAS.NATIONAL,
      SERVICE_AREAS.STATEWIDE,
    ]),
  }).isRequired,
};

export const BuilderProfilePersonalForm = props => {
  const {
    classes,
    isUpdatingPersonalProfile,
    personalProfileFormMode,
    onEditUserProfile,
    onEditUserProfileCancelled,
    onResetForm,
    handleSubmit,
    initialValues,
    builderExpDate,
    onUpdateUserProfile,
    enableEditing,
    businessAttributes: {
      businessApproved,
      showInSearch: businessShowInSearch,
    },
    // businessAttributes,
  } = props;

  // console.log('businessAttributes', businessAttributes.showInSearch);
  // const [isDeactivate, setIsDeactivate] = useState(initialValues.deactivate);
  // const [isOthers, setIsOthers] = useState(null);
  // const [reason, setReason] = useState('');
  const [showInSearch, setShowInSearch] = useState(businessShowInSearch);

  useEffect(
    () => () => {
      onResetForm(FORMS.BUILDER_PROFILE_PERSONAL);
      onEditUserProfileCancelled(USER_PROFILE_TYPES.PERSONAL);
    },
    [],
  );
  useEffect(() => {
    setShowInSearch(businessShowInSearch);
  }, [businessShowInSearch]);

  const handleFormSubmitted = values => {
    console.log({ values });
    // update email which is used for user explicitly
    const updatedValues = {
      ...values,
      email: values['custom:personalEmail'],
    };

    // Add country code to primary phone number
    // eslint-disable-next-line prettier/prettier
    updatedValues['custom:personalPhone'] = preparePhoneNumberForBackend(
      updatedValues['custom:personalPhone'],
    );

    updatedValues['custom:buildingLicExp'] = moment(
      updatedValues['custom:buildingLicExp'],
      'DD-MM-YYYY',
    ).format('YYYY-MM-DD');

    if (!values['custom:mobileNumber']) {
      updatedValues['custom:smsNotiStatus'] = NOTIFICATION_STATUSES.DISABLED;
    } else {
      // Add country code to mobile phone number
      // eslint-disable-next-line prettier/prettier
      updatedValues['custom:mobileNumber'] = preparePhoneNumberForBackend(
        updatedValues['custom:mobileNumber'],
      );
    }
    const businessDetails = {
      businessNumber: updatedValues.sub,
      phoneNumber:
        updatedValues['custom:mobileNumber'] ||
        updatedValues['custom:personalPhone'],
    };
    if (showInSearch !== businessShowInSearch) {
      businessDetails.showInSearch = showInSearch;
    }
    delete updatedValues.sub;

    const previousValues = {
      ...initialValues,
      email: initialValues['custom:personalEmail'],
    };

    // Add country code to primary phone number
    // eslint-disable-next-line prettier/prettier
    previousValues['custom:personalPhone'] = preparePhoneNumberForBackend(
      previousValues['custom:personalPhone'],
    );

    previousValues['custom:buildingLicExp'] = moment(
      builderExpDate,
      'DD-MM-YYYY',
    ).format('YYYY-MM-DD');

    if (!initialValues['custom:mobileNumber']) {
      previousValues['custom:smsNotiStatus'] = NOTIFICATION_STATUSES.DISABLED;
    } else {
      // Add country code to mobile phone number
      // eslint-disable-next-line prettier/prettier
      previousValues['custom:mobileNumber'] = preparePhoneNumberForBackend(
        previousValues['custom:mobileNumber'],
      );
    }
    delete previousValues.sub;

    // console.log({
    //   updatedValues,
    //   previousValues,
    //   initialValues,
    //   builderExpDate,
    // });
    // console.log(businessDetails, 'businessDetails');
    onUpdateUserProfile(
      localStorage.getItem('accessToken'),
      USER_PROFILE_TYPES.PERSONAL,
      updatedValues,
      businessDetails,
      previousValues,
    );
  };

  const renderFormButton = () => {
    if (personalProfileFormMode === VIEWABLE_FORM_MODES.VIEWING) {
      return (
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleClick}
          fullWidth
          disabled={!enableEditing}
        >
          Edit
        </Button>
      );
    }

    return (
      <div className={classes.formButtons}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleFormCancel}
          disabled={!enableEditing || isUpdatingPersonalProfile}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleFormSubmitted)}
          disabled={!enableEditing || isUpdatingPersonalProfile}
        >
          {isUpdatingPersonalProfile && <CircularProgress size={20} />}
          {!isUpdatingPersonalProfile && 'Done'}
        </Button>
      </div>
    );
  };

  const handleFormCancel = e => {
    e.preventDefault();
    onResetForm(FORMS.BUILDER_PROFILE_PERSONAL);
    onEditUserProfileCancelled(USER_PROFILE_TYPES.PERSONAL);
  };

  const handleClick = () => {
    onEditUserProfile(USER_PROFILE_TYPES.PERSONAL);
  };

  const isEditable = personalProfileFormMode === VIEWABLE_FORM_MODES.EDITING;

  // console.log({ initialValues });

  return (
    <form onSubmit={handleSubmit}>
      {formFields.map(field => (
        <div className={classes.formGroup} key={field.name}>
          <Field
            name={field.name}
            component={field.component}
            props={field.props}
            validate={field.validate}
            isEditing={personalProfileFormMode === VIEWABLE_FORM_MODES.EDITING}
            textValue={initialValues[field.name]}
            initialValue={initialValues[field.name]}
            normalize={field.normalize}
          />
        </div>
      ))}
      <div className={classes.formGroup}>
        <Field
          name="showInSearch"
          component={() => (
            <Switch
              name="showInSearch"
              disabled={!isEditable}
              checked={showInSearch}
              onChange={e => setShowInSearch(e.target.checked)}
            />
          )}
        />
        <span className={classes.textLabel}>Show In Search</span>
      </div>
      {/* <div className={`${classes.selectFormGroup} ${classes.formGroup} `}>
        <Field
          required
          name="deactivate"
          component={renderCheckbox}
          label="DEACTIVATE"
          className={classes.field}
          value={isDeactivate}
          onChange={(e) => setIsDeactivate(e.target.checked)}
          inputProps={{
            type: 'checkbox',
            required: 'required',
          }}
          disabled={personalProfileFormMode !== VIEWABLE_FORM_MODES.EDITING}
        />
      </div>
      {isDeactivate && (
        <div className={`${classes.selectFormGroup} ${classes.formGroup} `}>
          {personalProfileFormMode === VIEWABLE_FORM_MODES.EDITING ? (
            <Field
              name="reason"
              component={SelectFormField}
              props={{
                id: 'reason-drop',
                label: 'SELECT REASON',
                fullWidth: true,
              }}
              value={reason}
              // initialValue={reason}
              onChange={(e, value) => {
                setIsOthers(value === 'Others');
                setReason(value);
              }}
              validate={isDeactivate && [required]}
            >
              <option value=""></option>
              <option value="Invalid">Invalid</option>
              <option value="Does not want leads`">Does not want leads</option>
              <option value="Temporarily paused">Temporarily paused</option>
              <option value="Others">Others</option>
            </Field>
          ) : (
            <div className={classes.textWrapper}>
              <div className={classes.textLabel}>SELECT REASON</div>
              <div className={classes.textValue}>{reason}</div>
            </div>
          )}
        </div>
      )}
      {isDeactivate && isOthers && isEditable && (
        <Field
          style={{ marginTop: '20px' }}
          name="other_reason"
          component={ViewableTextField}
          props={{
            id: 'other-text-field',
            label: 'ENTER REASON',
            fullWidth: true,
          }}
          validate={[required]}
          isEditing={isEditable}
          textValue={initialValues.other_reason}
          initialValue={initialValues.other_reason}
          oldValue={initialValues.other_reason}
        />
      )} */}
      {businessApproved === 0 ? (
        <div className={classes.approvalStatusContainer}>
          <div className={classes.approvalStatus}>Pending approval</div>
          <Typography
            className={classes.approvalStatusText}
            variant="body2"
            color="textPrimary"
          >
            Your profile is being reviewed by James Hardie Australia. We will
            notify you via email once our review is complete.
          </Typography>
        </div>
      ) : (
        ''
      )}

      {businessApproved === 2 ? (
        <div className={classes.approvalStatusContainer}>
          <div className={classes.approvalStatus}>DEACTIVATED</div>
          <Typography
            className={classes.approvalStatusText}
            variant="body2"
            color="textPrimary"
          >
            Your profile is deactivated.
          </Typography>
        </div>
      ) : (
        ''
      )}
      <div className={classes.actionsRow}>{renderFormButton()}</div>
    </form>
  );
};

BuilderProfilePersonalForm.propTypes = propTypes;

const mapStateToProps = createStructuredSelector({
  isUpdatingPersonalProfile: selectIsUpdatingPersonalProfile(),
  personalProfileFormMode: selectPersonalProfileFormMode(),
  businessAttributes: selectBusinessAttributes(),
});

const mapDispatchToProps = dispatch => ({
  onEditUserProfile: profileType => dispatch(editUserProfile(profileType)),
  onEditUserProfileCancelled: profileType =>
    dispatch(editUserProfileCancelled(profileType)),
  onResetForm: form => dispatch(reset(form)),
  onUpdateUserProfile: (
    accessToken,
    profileType,
    userAttributes,
    businessAttributes,
    previousValues,
  ) =>
    dispatch(
      updateUserProfile(
        accessToken,
        profileType,
        userAttributes,
        businessAttributes,
        previousValues,
      ),
    ),
});

export default reduxForm({
  form: FORMS.BUILDER_PROFILE_PERSONAL,
  asyncBlurFields: ['custom:personalEmail'],
})(
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(BuilderProfilePersonalForm),
  ),
);
